import React, {
    useContext, useEffect, useMemo, useRef, useState 
} from 'react';
import {
    Box, Slide, Typography, useTheme 
} from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import CardContainer from '../../components/cardContainer';
import Card from '../../components/card';
import ProgressBar from '../../components/progressBar';
import {
    DiagnosticFormContext 
} from '../../../../contexts/form';
import {
    TypographyVariants, TypographyWeights 
} from '../../../../figmaTheme';

type Props = {
    position: number
};

export const DrinkJourney = ({
    position
}: Props): JSX.Element => {
    const formContext = useContext(DiagnosticFormContext);
    const drinksBaseline = useMemo(() => {
        return formContext.answers?.totalDrinksBaseline;
    }, [formContext.answers?.totalDrinksBaseline]);
    const [citationHeight, setCitationHeight] = useState(null);
    const initialScreenHeight = useMemo(() => {
        if (typeof window !== 'undefined') {
            return window.innerHeight;
        }
        return 0;
    }, []);
    const [screenHeight, setScreenHeight] = useState(initialScreenHeight);
    const [textSectionHeight, setTextSectionHeight] = useState(null);
    const [skipAnimation, setSkipAnimation] = useState(false);
    const useStyles = useMemo(() => makeStyles((theme) => ({
        scrollableContainer: {
            overflow: 'scroll',
            scrollbarWidth: 'none',
            minHeight: '100%',
            width: '100vw',
            maxWidth: '692px',
            transform: 'translate(-24px, 0)',
            padding: '0 24px'
        },
        content: {
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center'
        },
        textSection: {
            display: 'flex',
            flexDirection: 'column',
            rowGap: '16px',
            marginBottom: '16px'
        },
        graph: {
            position: 'absolute',
            width: '100%',
            transform: `translate(0, ${Math.max(screenHeight - 580, textSectionHeight + 16)}px)`,
            opacity: 0,
            animation: `$fadeIn ${skipAnimation ? 0 : 0.5}s forwards ${skipAnimation ? 0 : 0.5}s`
        },
        startLine: {
            animation: `$snake ${skipAnimation ? 0 : 1}s forwards ${skipAnimation ? 0 : 1}s, $fadeOut ${skipAnimation ? 0 : 0.5}s forwards ${skipAnimation ? 0 : 2.5}s`
        },
        startPoint: {
            opacity: 0,
            animation: `$fadeIn ${skipAnimation ? 0 : 0.5}s forwards ${skipAnimation ? 0 : 0.5}s, $minimise ${skipAnimation ? 0 : 0.5}s forwards ${skipAnimation ? 0 : 2.5}s`
        },
        startLabel: {
            fontFamily: 'Poppins',
            fontSize: '14px',
            lineHeight: '20px',
            fill: theme.palette.transparent,
            stroke: theme.palette.transparent,
            animation: `$appear ${skipAnimation ? 0 : 0.5}s forwards ${skipAnimation ? 0 : 1.5}s, $fade ${skipAnimation ? 0 : 0.5}s forwards ${skipAnimation ? 0 : 2.5}s`
        },
        startLabel2: {
            fontFamily: 'Poppins',
            fontSize: '14px',
            lineHeight: '20px',
            fill: theme.palette.transparent,
            stroke: theme.palette.transparent,
            animation: `$appearBlack ${skipAnimation ? 0 : 0.5}s forwards ${skipAnimation ? 0 : 1.5}s, $fadeBlack ${skipAnimation ? 0 : 0.5}s forwards ${skipAnimation ? 0 : 2.5}s`
        },
        firstStartLine: {
            animation: `$appear ${skipAnimation ? 0 : 0.5}s forwards ${skipAnimation ? 0 : 1.5}s, $fade ${skipAnimation ? 0 : 0.5}s forwards ${skipAnimation ? 0 : 2.5}s`
        },
        startValue: {
            fontFamily: 'Poppins',
            fontSize: '32px',
            lineHeight: '40px',
            fill: theme.palette.transparent,
            stroke: theme.palette.transparent,
            animation: `$appearBlack ${skipAnimation ? 0 : 0.5}s forwards ${skipAnimation ? 0 : 1.5}s, $fadeBlack ${skipAnimation ? 0 : 0.5}s forwards ${skipAnimation ? 0 : 2.5}s`
        },
        endLabel: {
            fontFamily: 'Poppins',
            fontSize: '14px',
            lineHeight: '20px',
            fill: theme.palette.transparent,
            stroke: theme.palette.transparent,
            animation: `$appear ${skipAnimation ? 0 : 0.5}s forwards ${skipAnimation ? 0 : 4}s`
        },
        endLabel2: {
            fontFamily: 'Poppins',
            fontSize: '14px',
            lineHeight: '20px',
            fill: theme.palette.transparent,
            stroke: theme.palette.transparent,
            animation: `$appearBlack ${skipAnimation ? 0 : 0.5}s forwards ${skipAnimation ? 0 : 4}s`
        },
        endLine: {
            animation: `$snake2 ${skipAnimation ? 0 : 1}s forwards ${skipAnimation ? 0 : 3.5}s`
        },
        endPoint: {
            opacity: 0,
            animation: `$fadeIn ${skipAnimation ? 0 : 0.5}s forwards ${skipAnimation ? 0 : 3}s`
        },
        minimise: {
            r: 10,
            fill: theme.palette.secondary['200'],
            transition: `r ${skipAnimation ? 0 : 0.5}s, fill ${skipAnimation ? 0 : 0.5}s`
        },
        citation: {
            position: 'absolute',
            width: 'calc(100% - 48px)',
            transform: `translate(0, ${Math.max(screenHeight - 580, textSectionHeight + 16) + 410 - citationHeight}px)`,
            opacity: 0,
            animation: `$fadeIn ${skipAnimation ? 0 : 0.5}s forwards ${skipAnimation ? 0 : 4.5}s`
        },
        '@keyframes appear': {
            '0%': {
                fill: theme.palette.transparent 
            },
            '100%': {
                fill: theme.palette.secondary.main 
            },
        },
        '@keyframes appearBlack': {
            '0%': {
                fill: theme.palette.transparent 
            },
            '100%': {
                fill: theme.palette.neutral.main 
            },
        },
        '@keyframes fade': {
            '0%': {
                fill: theme.palette.secondary.main 
            },
            '100%': {
                fill: theme.palette.secondary['200'] 
            },
        },
        '@keyframes fadeBlack': {
            '0%': {
                fill: theme.palette.neutral.main 
            },
            '100%': {
                fill: theme.palette.neutral['200'] 
            },
        },
        '@keyframes lighten': {
            '0%': {
                fontWeight: TypographyWeights.BOLD 
            },
            '100%': {
                fontWeight: TypographyWeights.REGULAR 
            },
        },
        '@keyframes shiftStartLabel': {
            '0%': {
                transform: 'translate(0)' 
            },
            '100%': {
                transform: 'translate(0, 10px)' 
            },
        },
        '@keyframes shrink': {
            '0%': {
                fontSize: '32px',
                lineHeight: '40px' 
            },
            '100%': {
                fontSize: '18px',
                lineHeight: '24px' 
            },
        },
        '@keyframes fadeIn': {
            '0%': {
                opacity: 0 
            },
            '100%': {
                opacity: 1 
            },
        },
        '@keyframes fadeOut': {
            '0%': {
                opacity: 1 
            },
            '100%': {
                opacity: 0 
            },
        },
        '@keyframes snake': {
            '0%': {
                strokeDashoffset: '120px' 
            },
            '100%': {
                strokeDashoffset: '60px' 
            },
        },
        '@keyframes snake2': {
            '0%': {
                strokeDashoffset: '376px' 
            },
            '100%': {
                strokeDashoffset: '188px' 
            },
        },
        '@keyframes minimise': {
            '0%': {
                r: 16,
                fill: theme.palette.secondary.main 
            },
            '100%': {
                r: 10,
                fill: theme.palette.secondary['200'] 
            },
        }
    })), [screenHeight, citationHeight, textSectionHeight, skipAnimation]);
    const classes = useStyles();
    const theme = useTheme();

    const onClick = () => {
        if (!skipAnimation) setSkipAnimation(true);
        else formContext.goNext();
    };

    useEffect(() => {
        if (typeof window === 'undefined') return;

        const resizeListener = () => {
            setScreenHeight(window.innerHeight);
        };
        window.addEventListener('resize', resizeListener);

        setTimeout(() => {
            setSkipAnimation(true);
        }, 5000);

        return () => {
            window.removeEventListener('resize', resizeListener);
        };
    }, []);

    const citation = useRef(null);
    const textSection = useRef(null);

    useEffect(() => {
        if (!citation.current) return;

        setCitationHeight(citation.current.clientHeight);
    }, [citation.current]);

    useEffect(() => {
        if (!textSection.current) return;

        setTextSectionHeight(textSection.current.clientHeight);
    }, [textSection.current]);

    return (
        <CardContainer
            sectionKey={'habits'}
            goBack={formContext.goBack}
            goNext={onClick}
        >
            <Card>
                <ProgressBar
                    currentPosition={position}
                    sectionKey={'habits'}
                />
                <Box className={classes.scrollableContainer}>
                    <Box className={classes.content}>
                        <Slide
                            in={true}
                            direction={formContext.screenDirection}
                            timeout={500}
                            mountOnEnter
                            unmountOnExit
                        >
                            <Box
                                className={classes.textSection}
                                ref={textSection}
                            >
                                <Typography
                                    variant={TypographyVariants.H3}
                                    dangerouslySetInnerHTML={{
                                        __html: `On average, Sunnyside members reduce their <span style="font-weight:${TypographyWeights.BOLD};color:${theme.palette.secondary.main}">weekly</span> consumption by <span style="font-weight:${TypographyWeights.BOLD};color:${theme.palette.secondary.main}">32% in the first 30 days.<span style="font-size:10px;line-height:16px;font-weight:${TypographyWeights.SEMI_BOLD};vertical-align:super">†</span></span>` 
                                    }}
                                />
                                <Typography
                                    variant={TypographyVariants.H6}
                                    dangerouslySetInnerHTML={{
                                        __html: 'Here\'s what this could look like for you:' 
                                    }}
                                />
                            </Box>
                        </Slide>
                        <svg
                            className={classes.graph}
                            width="375"
                            height="434"
                            viewBox="79 -80 375 334"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                        >
                            <text
                                className={`${classes.startLabel} ${classes.firstStartLine}`}
                                fill={theme.palette.transparent}
                                x="109"
                                y="-72"
                                fontFamily={'Poppins'}
                                fontSize={'14px'}
                            >
                                Your Starting Point
                            </text>
                            <text
                                className={classes.startLabel2}
                                fill={theme.palette.transparent}
                                x="109"
                                y="-40"
                                fontFamily={'Poppins'}
                                fontSize={'14px'}
                            >
                                <tspan
                                    fontSize={'32px'}
                                    fontWeight={TypographyWeights.BOLD}
                                    className={classes.startValue}
                                >
                                    {drinksBaseline}
                                </tspan>
                                {' '}
                                weekly drinks
                            </text>
                            <line
                                x1="109"
                                y1="12"
                                x2="109"
                                y2="-30"
                                stroke={'#121C274D'}
                                strokeDasharray={'3px'}
                                fill="none"
                            />
                            <line
                                className={classes.startLine}
                                x1="109"
                                y1="12"
                                x2="109"
                                y2="-30"
                                stroke={theme.palette.white}
                                strokeWidth={'2px'}
                                strokeDasharray={'60px'}
                                strokeDashoffset={'120px'}
                            />

                            <text
                                className={classes.endLabel}
                                fill={theme.palette.transparent}
                                x="280"
                                y="-2"
                                fontFamily={'Poppins'}
                                fontSize={'14px'}
                            >
                                In 30 Days
                            </text>
                            <text
                                className={classes.endLabel2}
                                fill={theme.palette.transparent}
                                x="280"
                                y="30"
                                fontFamily={'Poppins'}
                                fontSize={'14px'}
                            >
                                <tspan
                                    fontSize={'32px'}
                                    fontWeight={TypographyWeights.BOLD}
                                >
                                    {Math.floor(drinksBaseline * 0.68)}
                                </tspan>
                                {' '}
                                weekly drinks
                            </text>
                            <line
                                x1="280"
                                y1="125"
                                x2="280"
                                y2="40"
                                stroke={'#121C274D'}
                                strokeDasharray={'3px'}
                                fill="none"
                            />
                            <line
                                className={classes.endLine}
                                x1="280"
                                y1="125"
                                x2="280"
                                y2="40"
                                stroke={theme.palette.white}
                                strokeWidth={'2px'}
                                strokeDasharray={'188px'}
                                strokeDashoffset={'376px'}
                            />

                            <path
                                d="M0 0.0732826C0 0.0732826 153.975 -5.52152 228.693 81.0591C287.296 148.965 366.875 179.123 420.939 179.123C459.555 179.123 536 179.123 536 179.123V425.168C536 427.378 534.209 429.168 532 429.168H4C1.79086 429.168 0 427.378 0 425.168V0.0732826Z"
                                fill="url(#paint0_linear_1218_29330)"
                            />
                            <circle
                                className={classes.startPoint}
                                cx="109"
                                cy="12"
                                r="16"
                                strokeWidth={3}
                                stroke={theme.palette.white}
                                fill={theme.palette.secondary.main}
                            />
                            <circle
                                className={classes.endPoint}
                                cx="280"
                                cy="125"
                                r="16"
                                strokeWidth={3}
                                stroke={theme.palette.white}
                                fill={theme.palette.secondary.main}
                            />
                            <defs>
                                <linearGradient
                                    id="paint0_linear_1218_29330"
                                    x1="306.617"
                                    y1="106.076"
                                    x2="261.435"
                                    y2="431.476"
                                    gradientUnits="userSpaceOnUse"
                                >
                                    <stop
                                        stopColor="#9747FF"
                                        stopOpacity="0.6"
                                    />
                                    <stop
                                        offset="0.527373"
                                        stopColor="#9747FF"
                                        stopOpacity="0.16"
                                    />
                                    <stop
                                        offset="1"
                                        stopColor="#9747FF"
                                        stopOpacity="0"
                                    />
                                </linearGradient>
                            </defs>
                        </svg>
                        <Typography
                            ref={citation}
                            variant={TypographyVariants.VERY_SMALL}
                            className={classes.citation}
                        >
                            <sup>
                                †
                            </sup>
                            {' '}
                            Nehal P. Vadhan, PhD, et al. (2024). Self-reported alcohol consumption during participation in a text messaging-based online drinking moderation platform. Alcohol: Clinical and Experimental Research.
                        </Typography>
                    </Box>
                    <Box
                        style={{
                            height: `${Math.max(screenHeight - 580, textSectionHeight + 16) + 730}px` 
                        }}
                    />
                </Box>
            </Card>
        </CardContainer>
    );
};

export default DrinkJourney;
